import { Layout } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory, useLocation } from 'react-router-dom';
import HeaderBar from '../../Layout/HeaderBar';
import FooterBar from '../../Layout/FooterBar';
import { useMonitoringOverview } from '../Monitoring/useMonitoringOverview';
import { anomalyQueryKey, useAnomalies } from '../Monitoring/anomalies/useAnomalies';
import { useFleetSensors } from './useFleetSensors';
import { buildFleetDevices, type FleetDevice } from './model';
import FleetOverview from './FleetOverview';
import DeviceOverviewDrawer from './DeviceOverviewDrawer';
import type { DeviceWorkspaceView } from '../Monitoring/UnifiedAlerts/DeviceWorkspace';
import { useDeviceHeartbeats } from '../Monitoring/Heartbeat/useDeviceHeartbeats';

export default function SensorOverviewPage() {
  const client = useQueryClient();
  const inventory = useFleetSensors();
  const monitoring = useMonitoringOverview();
  const anomalies = useAnomalies();
  const heartbeats = useDeviceHeartbeats();
  const history = useHistory();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const requestedView = params.get('view');
  const view: DeviceWorkspaceView =
    requestedView === 'settings' || requestedView === 'incidents' ? requestedView : 'status';
  const selected = buildFleetDevices(
    inventory.data || [],
    monitoring.data,
    anomalies.events,
    heartbeats.data
  ).find(
    (device) => device.orgId === params.get('orgId') && device.sensorId === params.get('sensorId')
  );
  const monitoringState = {
    loading: monitoring.isPending,
    error: monitoring.isError ? monitoring.error.message : null,
  };
  function select(device: FleetDevice, nextView: DeviceWorkspaceView = 'status') {
    history.push({
      pathname: '/sensors',
      search: new URLSearchParams({
        orgId: device.orgId,
        sensorId: device.sensorId,
        view: nextView,
      }).toString(),
    });
  }
  function refresh() {
    void inventory.refetch();
    void monitoring.refetch();
    void heartbeats.refetch();
    void client.invalidateQueries({ queryKey: anomalyQueryKey });
  }
  return (
    <Layout>
      <HeaderBar />
      <Layout.Content>
        <FleetOverview
          sensors={inventory.data || []}
          monitoring={monitoring.data}
          anomalies={anomalies.events}
          heartbeats={heartbeats.data}
          heartbeatState={{
            loading: heartbeats.isPending,
            error: heartbeats.isError ? heartbeats.error.message : null,
          }}
          scan={anomalies.scan}
          inventoryState={{
            loading: inventory.isPending,
            error: inventory.isError ? inventory.error.message : null,
          }}
          monitoringState={monitoringState}
          anomalyState={{
            loading: anomalies.query.isPending,
            error: anomalies.query.isError ? anomalies.query.error.message : null,
          }}
          hasMoreAnomalies={Boolean(anomalies.query.hasNextPage)}
          loadingMore={anomalies.query.isFetchingNextPage}
          refreshing={
            inventory.isFetching ||
            monitoring.isFetching ||
            anomalies.query.isFetching ||
            heartbeats.isFetching
          }
          onRefresh={refresh}
          onLoadMore={() => {
            void anomalies.query.fetchNextPage();
          }}
          onSelect={select}
          onConfigure={(device) => select(device, 'settings')}
          initialOrgId={!params.get('sensorId') ? params.get('orgId') || undefined : undefined}
        />
        {selected && (
          <DeviceOverviewDrawer
            key={selected.key}
            device={selected}
            view={view}
            onViewChange={(nextView) => select(selected, nextView)}
            monitoring={monitoring.data}
            monitoringState={monitoringState}
            onClose={() => history.replace('/sensors')}
          />
        )}
      </Layout.Content>
      <FooterBar />
    </Layout>
  );
}
